/**
 * Service API
 *
 * RTK Query API for Service operations.
 * Provides hooks for CRUD operations on services.
 * Service includes: Pre-Commissioning (Section 1), Commissioning (Section 2), Warranty Certificate (Section 3)
 */

import { baseApi } from './baseApi';
import {
  CreateServiceRequest,
  UpdateServiceRequest,
  UpdateCommissioningRequest,
  UpdateWarrantyRequest,
  ListServiceRequest,
  ServiceResponse,
  ServiceListResponse,
  DeleteServiceResponse,
} from '@OrianaTypes';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const serviceApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * GET /service
     * Fetch paginated list of services
     */
    getServices: builder.query<ServiceListResponse, ListServiceRequest | void>({
      query: (params) => ({
        url: '/service',
        params: params || {},
      }),
      transformResponse: (response: ApiResponse<ServiceResponse[]>) => ({
        data: response.data,
        pagination: response.pagination || {
          page: 1,
          limit: 20,
          total: response.data.length,
          totalPages: 1,
        },
      }),
      providesTags: (result) =>
        result
          ? [
            ...result.data.map(({ serviceId }) => ({
              type: 'Service' as const,
              id: serviceId,
            })),
            { type: 'Service', id: 'LIST' },
          ]
          : [{ type: 'Service', id: 'LIST' }],
    }),

    /**
     * GET /service/dispatch/:dispatchId
     * Fetch all services for a specific dispatch
     */
    getServicesByDispatchId: builder.query<ServiceResponse[], number>({
      query: (dispatchId) => `/service/dispatch/${dispatchId}`,
      transformResponse: (response: ApiResponse<ServiceResponse[]>) => response.data,
      providesTags: (result, _error, dispatchId) =>
        result
          ? [
            ...result.map(({ serviceId }) => ({
              type: 'Service' as const,
              id: serviceId,
            })),
            { type: 'Service', id: `DISPATCH-${dispatchId}` },
          ]
          : [{ type: 'Service', id: `DISPATCH-${dispatchId}` }],
    }),

    /**
     * GET /service/po/:poId
     * Fetch all services for a specific PO (via dispatch relation)
     */
    getServicesByPoId: builder.query<ServiceResponse[], string>({
      query: (poId) => `/service/po/${poId}`,
      transformResponse: (response: ApiResponse<ServiceResponse[]>) => response.data,
      providesTags: (result, _error, poId) =>
        result
          ? [
            ...result.map(({ serviceId }) => ({
              type: 'Service' as const,
              id: serviceId,
            })),
            { type: 'Service', id: `PO-${poId}` },
          ]
          : [{ type: 'Service', id: `PO-${poId}` }],
    }),

    /**
     * GET /service/:id
     * Fetch a single service by ID
     */
    getServiceById: builder.query<ServiceResponse, number>({
      query: (id) => `/service/${id}`,
      transformResponse: (response: ApiResponse<ServiceResponse>) => response.data,
      providesTags: (_result, _error, id) => [{ type: 'Service', id }],
    }),

    /**
     * POST /service
     * Create a new service (Section 1: Pre-Commissioning only)
     */
    createService: builder.mutation<
      ServiceResponse,
      CreateServiceRequest & { poId?: string }
    >({
      query: (newService) => {
        // Extract poId from the request (won't be sent to backend)
        const { poId, ...serviceData } = newService;
        return {
          url: '/service',
          method: 'POST',
          body: serviceData,
        };
      },
      transformResponse: (response: ApiResponse<ServiceResponse>) =>
        response?.data ?? response,
      invalidatesTags: (result, _error, { dispatchId, poId }) => {
        const tags: Array<{ type: 'Service' | 'PO' | 'Dispatch'; id: string | number }> = [
          { type: 'Service', id: 'LIST' },
          { type: 'Service', id: `DISPATCH-${dispatchId}` },
        ];
        if (poId) {
          tags.push({ type: 'Service', id: `PO-${poId}` });
          tags.push({ type: 'PO', id: poId });
        }
        return tags;
      },
    }),

    /**
     * PUT /service/:id
     * Update service (Section 1: Pre-Commissioning)
     */
    updateService: builder.mutation<
      ServiceResponse,
      { id: number; data: UpdateServiceRequest; poId?: string }
    >({
      query: ({ id, data }) => ({
        url: `/service/${id}`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: (response: ApiResponse<ServiceResponse>) =>
        response?.data ?? response,
      invalidatesTags: (result, _error, { id, poId }) => {
        const tags: Array<{ type: 'Service' | 'PO' | 'Dispatch'; id: string | number }> = [
          { type: 'Service', id },
          { type: 'Service', id: 'LIST' },
        ];
        if (result) {
          tags.push({ type: 'Service', id: `DISPATCH-${result.dispatchId}` });
        }
        if (poId) {
          tags.push({ type: 'Service', id: `PO-${poId}` });
          tags.push({ type: 'PO', id: poId });
        }
        return tags;
      },
    }),

    /**
     * PUT /service/:id/commissioning
     * Update commissioning (Section 2)
     */
    updateCommissioning: builder.mutation<
      ServiceResponse,
      { id: number; data: UpdateCommissioningRequest; poId?: string }
    >({
      query: ({ id, data }) => ({
        url: `/service/${id}/commissioning`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: (response: ApiResponse<ServiceResponse>) => response.data,
      invalidatesTags: (result, _error, { id, poId }) => {
        const tags: Array<{ type: 'Service' | 'PO' | 'Dispatch'; id: string | number }> = [
          { type: 'Service', id },
          { type: 'Service', id: 'LIST' },
        ];
        if (result) {
          tags.push({ type: 'Service', id: `DISPATCH-${result.dispatchId}` });
        }
        if (poId) {
          tags.push({ type: 'Service', id: `PO-${poId}` });
          tags.push({ type: 'PO', id: poId });
        }
        return tags;
      },
    }),

    /**
     * PUT /service/:id/warranty
     * Update warranty certificate (Section 3)
     */
    updateWarranty: builder.mutation<
      ServiceResponse,
      { id: number; data: UpdateWarrantyRequest; poId?: string }
    >({
      query: ({ id, data }) => ({
        url: `/service/${id}/warranty`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: (response: ApiResponse<ServiceResponse>) => response.data,
      invalidatesTags: (result, _error, { id, poId }) => {
        const tags: Array<{ type: 'Service' | 'PO' | 'Dispatch'; id: string | number }> = [
          { type: 'Service', id },
          { type: 'Service', id: 'LIST' },
        ];
        if (result) {
          tags.push({ type: 'Service', id: `DISPATCH-${result.dispatchId}` });
        }
        if (poId) {
          tags.push({ type: 'Service', id: `PO-${poId}` });
          tags.push({ type: 'PO', id: poId });
        }
        return tags;
      },
    }),

    /**
     * DELETE /service/:id
     * Delete a service
     */
    deleteService: builder.mutation<
      DeleteServiceResponse,
      { id: number; dispatchId?: number; poId?: string }
    >({
      query: ({ id }) => ({
        url: `/service/${id}`,
        method: 'DELETE',
      }),
      transformResponse: (response: ApiResponse<DeleteServiceResponse>) => response.data,
      invalidatesTags: (_result, _error, { id, dispatchId, poId }) => {
        const tags: Array<{ type: 'Service' | 'PO' | 'Dispatch'; id: string | number }> = [
          { type: 'Service', id },
          { type: 'Service', id: 'LIST' },
        ];
        if (dispatchId) {
          tags.push({ type: 'Service', id: `DISPATCH-${dispatchId}` });
        }
        if (poId) {
          tags.push({ type: 'Service', id: `PO-${poId}` });
          tags.push({ type: 'PO', id: poId });
        }
        return tags;
      },
    }),
  }),

  overrideExisting: false,
});

// Export hooks
export const {
  useGetServicesQuery,
  useGetServicesByDispatchIdQuery,
  useGetServicesByPoIdQuery,
  useGetServiceByIdQuery,
  useLazyGetServicesQuery,
  useLazyGetServicesByDispatchIdQuery,
  useLazyGetServicesByPoIdQuery,
  useLazyGetServiceByIdQuery,
  useCreateServiceMutation,
  useUpdateServiceMutation,
  useUpdateCommissioningMutation,
  useUpdateWarrantyMutation,
  useDeleteServiceMutation,
} = serviceApi;

